import { isEmpty, isFunction } from 'radash';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import {Combobox} from '@/components/core/Combobox';
import { useItemDetails } from '@/components/ItemDetails/ItemDetails.queries';
import { getInjector, getRootScope } from '@/services/angularjs-bridge.ts';
import { PersistedItem } from '@/types/DataModel';

import useFocusOnAttribute from '../hooks/useFocusOnAttribute';
import { useIsAttributeReadonly } from '../hooks/useIsAttributeReadonly';
import { useIsHiddenAttribute } from '../hooks/useIsHiddenAttribute';
import { usePlaceholder } from '../hooks/usePlaceholder';
import { useReferenceItemFormatter } from '../hooks/useReferenceItemFormatter';
import { useValidationsStore } from '../hooks/useValidationsStore';
import { Layout } from '../Layout/Layout';
import { useReferencedItems } from '../queries';
import { EditorComponent, Validation } from '../types';

interface SingleReferenceComponentProps extends EditorComponent {
    // String specific props go here
}

const SingleReferenceComponent = ({
    attribute,
    direction = 'horizontal',
    fieldPath = attribute.name,
    hidden = false,
    readonly = false,
    hideLabel = false,
    hasErrors: parentHasErrors = false,
    hasWarnings: parentHasWarnings = false,
    isKeyAttribute = false,
    beforeAttributeControl,
    handleUrlChange
}: SingleReferenceComponentProps) => {
    const $angularJsInjector = getInjector();
    const $rootScope = getRootScope($angularJsInjector);

    const focusRef = useFocusOnAttribute<HTMLDivElement>(attribute);
    const validationErrors = useValidationsStore(useShallow(state => state.validationErrors[fieldPath] as Validation[]));
    const validationWarnings = useValidationsStore(useShallow(state => state.validationWarnings[fieldPath] as Validation[]));
    const hasErrors = parentHasErrors || !isEmpty(validationErrors);
    const hasWarnings = parentHasWarnings || !isEmpty(validationWarnings);

    hidden = useIsHiddenAttribute(attribute, attribute.name, hidden);

    readonly = useIsAttributeReadonly(attribute, attribute.name, readonly);

    const filters = attribute.params?.filter;
    const filterFunctionName = attribute.params?.uiReferenceFilter;

    const [mappedOptions, setMappedOptions] =
        useState<{ key: string; label: string }[]>();

    const { control, watch, setValue, resetField, getValues } = useFormContext();
    const [searchKeyword, setSearchKeyword] = useState<string>();

    const placeholder = usePlaceholder(attribute, readonly);
    const { formatReferenceItem } = useReferenceItemFormatter(attribute);

    const containingItem = getValues() as PersistedItem;

    const handleSearchKeywordChange = useCallback((keyword: string) => {
        const searchQueryCustomFunction = $rootScope.getService(attribute?.params?.uiSearchQuery);

        if (isFunction(searchQueryCustomFunction)) {
            keyword = searchQueryCustomFunction(keyword, containingItem, attribute, $rootScope.user, $rootScope.organization);
        }
        setSearchKeyword(keyword);
    },
    [$rootScope, attribute, containingItem],
    );

    const createMappedOptions = (item: any) => {
        const formattedLabel = formatReferenceItem(item, {} as PersistedItem);
        const label =
            !isEmpty(filterFunctionName) || !isEmpty(filters)
                ? formattedLabel
                : `${item?.gtin} - ${item?.target_market}`;
        return {
            key: item.primaryKey__ as string,
            label: label ?? (item.primaryKey__ as string),
        };
    };

    const referenceItemCategories = attribute.params?.references;

    const { data: singleReferenceValues, isLoading } = useReferencedItems({
        variables: {
            keyword: searchKeyword ?? '',
            referenceableCategories: referenceItemCategories,
        },
        retry: false,
        enabled: searchKeyword !== undefined && searchKeyword?.length >= 3,
    });

    useEffect(() => {
        if (singleReferenceValues !== undefined) {
            setMappedOptions(singleReferenceValues.map(createMappedOptions));
        } else {
            setMappedOptions([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [singleReferenceValues]);

    const currentValue = watch(fieldPath, '');

    // "initialItem" is loaded when attribute is first rendered, if there is no
    // initial value associated with the attribute, initialItem is ignored and
    // initialItemLoaded is set to true (nothing to load)
    const [initialItemLoaded, setInitialItemLoaded] = useState<boolean>(currentValue === '');

    const { data: fetchedInitialItem, isPending: isLoadingInitialItem } = useItemDetails({
        variables: { primaryKey__ : currentValue },
        retry: false,
        enabled: !initialItemLoaded
    });

    useEffect(() => {
        if (fetchedInitialItem) {
            const mappedItem = createMappedOptions(fetchedInitialItem);
            if (mappedItem) {
                setMappedOptions([mappedItem]);
                resetField(fieldPath, {
                    defaultValue: mappedItem.key,
                    keepError: true,
                    keepDirty: true,
                    keepTouched: true,
                });
            }
            setInitialItemLoaded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchedInitialItem]);

    if (hidden) {
        return null;
    }

    return (
        <Layout
            attribute={attribute}
            hideLabel={hideLabel}
            direction={direction}
            fieldPath={fieldPath}
            gridMode={false}
            isKeyAttribute={isKeyAttribute}
            beforeAttributeControl={beforeAttributeControl}
            isClearable={!readonly}
        >
            <div className="tw-flex tw-flex-row">
                <Controller
                    control={control}
                    name={fieldPath}
                    render={() => (
                        <Combobox
                            id={fieldPath}
                            isDisabled={readonly}
                            ref={focusRef}
                            onFocus={handleUrlChange}
                            placeholder={placeholder}
                            isLoading={isLoading || (!initialItemLoaded && isLoadingInitialItem)}
                            options={mappedOptions}
                            filterOption={() => true}
                            value={currentValue === '' ? null : mappedOptions?.find(
                                (o) => o.key === currentValue,
                            )}
                            onChange={newValue => {
                                setValue(fieldPath, newValue?.key || '', { shouldDirty: true });
                            }}
                            onInputChange={textTyped => {
                                handleSearchKeywordChange(textTyped);
                            }}
                            inputControlClassnames={`${hasErrors ? 'error-input' : hasWarnings ? 'warning-input': ''}`}
                        />
                    )}
                />
            </div>
        </Layout>
    );
};

export { SingleReferenceComponent };
